import React, { Component } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import styled, { createGlobalStyle } from "styled-components";
import ReactGA from "react-ga4";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    margin: 0;
    padding: 0;
    min-height: 100%;
    width: 100%;
  }

  body {
    overflow-x: hidden;
    overflow-y: auto;
    background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
    font-family: Arial, sans-serif;
  }

  .leaflet-container {
    font-family: Arial, sans-serif;
    background: #0f1720;
  }

  .leaflet-control-attribution {
    font-size: 10px;
  }

  .leaflet-popup-content-wrapper,
  .leaflet-popup-tip {
    background: #111827;
    color: #fff;
  }

  .leaflet-top.leaflet-right {
    top: 10px;
    right: 10px;
  }

  .leaflet-control-zoom {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.24);
    border: none !important;
  }

  .leaflet-control-zoom a {
    background: rgba(255, 255, 255, 0.96) !important;
    color: #111 !important;
    border: none !important;
  }

  .leaflet-control-zoom a:hover {
    background: #fff !important;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
  overflow-x: hidden;
  overflow-y: auto;
`;

const BrandBadge = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(0, 125, 244, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #8ccbff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.3px;
  margin-bottom: 12px;
`;

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  max-width: 860px;
  padding: 20px 0 40px 0;
  box-sizing: border-box;
`;

const Card = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 14px;
  padding: 20px;
  margin: 20px;
  width: calc(100% - 40px);
  max-width: 680px;
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  box-sizing: border-box;
`;

const Hero = styled.div`
  position: relative;
  width: 100%;
  min-height: 240px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #0f1720 0%, #1c2737 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at top right,
      rgba(0, 125, 244, 0.2),
      transparent 35%
    ),
    linear-gradient(180deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.45) 100%);
  pointer-events: none;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 18px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const RunnerBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  background: rgba(0, 125, 244, 0.35);
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(props) =>
    props.live ? "rgba(0, 200, 120, 0.18)" : "rgba(255,255,255,0.10)"};
  color: #fff;
  border: 1px solid
    ${(props) =>
      props.live ? "rgba(0, 200, 120, 0.35)" : "rgba(255,255,255,0.14)"};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${(props) => (props.live ? "#00d17d" : "#c7d2de")};
  display: inline-block;
`;

const HeroMapShell = styled.div`
  position: relative;
  width: 100%;
  margin-top: 16px;
`;

const HeroMapWrap = styled.div`
  width: 100%;
  height: 260px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #0f1720;

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const MapTypeButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1000;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: rgba(17, 24, 39, 0.88);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(17, 24, 39, 0.96);
  }
`;

const MapTypeModalBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.28);
`;

const MapTypeSheet = styled.div`
  width: 100%;
  background: #fff;
  color: #111827;
  border-radius: 20px 20px 0 0;
  padding: 12px 14px 20px 14px;
  box-sizing: border-box;
`;

const SheetHandle = styled.div`
  width: 46px;
  height: 5px;
  border-radius: 999px;
  background: #d1d5db;
  margin: 0 auto 14px auto;
`;

const SheetTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 14px;
`;

const MapTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`;

const MapTypeCard = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  text-align: left;
  cursor: pointer;
`;

const MapTypeThumb = styled.div`
  width: 100%;
  height: 82px;
  border-radius: 12px;
  overflow: hidden;
  border: ${(props) =>
    props.active ? "2px solid #ff8a00" : "2px solid transparent"};
  box-sizing: border-box;
  background: #e5e7eb;
`;

const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const MapTypeLabel = styled.div`
  margin-top: 8px;
  font-size: 15px;
  font-weight: ${(props) => (props.active ? 800 : 600)};
  color: ${(props) => (props.active ? "#ff8a00" : "#111827")};
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const MetricLabel = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-bottom: 6px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 800;
`;

const Description = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin: 18px 0 10px 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const Button = styled.button`
  background-color: ${(props) => (props.secondary ? "transparent" : "#007df4")};
  color: white;
  border: ${(props) => (props.secondary ? "1px solid #1d9bf0" : "none")};
  border-radius: 10px;
  padding: 13px 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: ${(props) =>
      props.secondary ? "rgba(29,155,240,0.08)" : "#0068cc"};
  }
`;

const Section = styled.div`
  margin-top: 18px;
`;

const SimpleMapBox = styled.div`
  width: 100%;
  min-height: 220px;
  border-radius: 12px;
  background:
    linear-gradient(135deg, rgba(0, 125, 244, 0.1), rgba(255, 255, 255, 0.04)),
    rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px;
  box-sizing: border-box;
`;

const SmallText = styled.div`
  font-size: 12px;
  opacity: 0.82;
`;

const MAP_TYPES = {
  standard: {
    key: "standard",
    label: "Standard",
    maptilerId: "streets-v2",
    overlayId: null,
    thumb: "/map/standard.png",
  },
  satellite: {
    key: "satellite",
    label: "Satellite",
    maptilerId: "satellite",
    overlayId: null,
    thumb: "/map/satellite.png",
  },
  hybrid: {
    key: "hybrid",
    label: "Hybrid",
    maptilerId: "satellite",
    overlayId: "hybrid",
    thumb: "/map/hybrid.png",
  },
  outdoor: {
    key: "outdoor",
    label: "Outdoor",
    maptilerId: "outdoor-v2",
    overlayId: null,
    thumb: "/map/outdoor.png",
  },
};

class LiveRouteMap extends Component {
  constructor(props) {
    super(props);
    this.mapRef = React.createRef();
    this.map = null;
    this.baseTileLayer = null;
    this.overlayTileLayer = null;
    this.routeLine = null;
    this.liveMarker = null;
    this.hasFitOnce = false;
    this.userMovedMap = false;

    this.state = {
      mapType: "standard",
      showMapTypeSheet: false,
    };
  }

  componentDidMount() {
    this.initMap();
    this.renderRoute();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.mapType !== this.state.mapType) {
      this.applyMapType();
    }

    if (
      prevProps.points !== this.props.points ||
      prevProps.lastPoint !== this.props.lastPoint ||
      prevProps.isLive !== this.props.isLive
    ) {
      this.renderRoute();
    }
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.off("dragstart", this.handleUserMovedMap);
      this.map.off("zoomstart", this.handleUserMovedMap);
      this.map.remove();
      this.map = null;
    }
  }

  handleUserMovedMap = () => {
    this.userMovedMap = true;
  };

  smoothLatLngs = (latLngs = []) => {
    if (!Array.isArray(latLngs) || latLngs.length < 3) return latLngs;

    const result = [latLngs[0]];

    for (let i = 1; i < latLngs.length - 1; i++) {
      const prev = latLngs[i - 1];
      const curr = latLngs[i];
      const next = latLngs[i + 1];

      result.push([
        (prev[0] + curr[0] + next[0]) / 3,
        (prev[1] + curr[1] + next[1]) / 3,
      ]);
    }

    result.push(latLngs[latLngs.length - 1]);
    return result;
  };

  initMap = () => {
    if (!this.mapRef.current || this.map) return;

    this.map = L.map(this.mapRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([31.7683, 35.2137], 13);

    this.applyMapType();

    this.map.on("dragstart", this.handleUserMovedMap);
    this.map.on("zoomstart", this.handleUserMovedMap);
  };

  getTileUrl = (maptilerId) => {
    const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY;
    return `https://api.maptiler.com/maps/${maptilerId}/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}&language=en`;
  };

  createBaseLayer = (maptilerId) => {
    return L.tileLayer(this.getTileUrl(maptilerId), {
      maxZoom: 20,
      attribution:
        '&copy; <a href="https://www.maptiler.com/copyright/" target="_blank">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap contributors</a>',
      crossOrigin: true,
    });
  };

  applyMapType = () => {
    if (!this.map) return;

    const config = MAP_TYPES[this.state.mapType] || MAP_TYPES.standard;

    if (this.baseTileLayer) {
      this.map.removeLayer(this.baseTileLayer);
      this.baseTileLayer = null;
    }

    if (this.overlayTileLayer) {
      this.map.removeLayer(this.overlayTileLayer);
      this.overlayTileLayer = null;
    }

    this.baseTileLayer = this.createBaseLayer(config.maptilerId);
    this.baseTileLayer.addTo(this.map);

    if (config.overlayId) {
      this.overlayTileLayer = this.createBaseLayer(config.overlayId);
      this.overlayTileLayer.addTo(this.map);
    }
  };

  getLatLngs = () => {
    const { points = [] } = this.props;

    return points
      .filter(
        (p) =>
          p &&
          typeof p.lat === "number" &&
          typeof p.lng === "number" &&
          !Number.isNaN(p.lat) &&
          !Number.isNaN(p.lng),
      )
      .map((p) => [p.lat, p.lng]);
  };

  renderRoute = () => {
    if (!this.map) return;

    const rawLatLngs = this.getLatLngs();
    const latLngs = this.smoothLatLngs(rawLatLngs);

    if (this.routeLine) {
      this.map.removeLayer(this.routeLine);
      this.routeLine = null;
    }

    if (this.liveMarker) {
      this.map.removeLayer(this.liveMarker);
      this.liveMarker = null;
    }

    if (!latLngs.length) return;

    this.routeLine = L.polyline(latLngs, {
      color: "#007df4",
      weight: 5,
      opacity: 0.95,
      lineCap: "round",
      lineJoin: "round",
    }).addTo(this.map);

    const { lastPoint, isLive } = this.props;

    const finalPoint =
      lastPoint &&
      typeof lastPoint.lat === "number" &&
      typeof lastPoint.lng === "number"
        ? [lastPoint.lat, lastPoint.lng]
        : latLngs[latLngs.length - 1];

    this.liveMarker = L.circleMarker(finalPoint, {
      radius: 8,
      weight: 3,
      color: "#ffffff",
      fillColor: isLive ? "#00d17d" : "#007df4",
      fillOpacity: 1,
    }).addTo(this.map);

    this.liveMarker.bindPopup(isLive ? "Live position" : "Final position");

    if (!this.hasFitOnce) {
      if (latLngs.length === 1) {
        this.map.setView(latLngs[0], 15);
      } else {
        this.map.fitBounds(this.routeLine.getBounds(), {
          padding: [30, 30],
        });
      }
      this.hasFitOnce = true;
      return;
    }

    if (isLive && !this.userMovedMap) {
      this.map.panTo(finalPoint, { animate: true });
    }
  };

  toggleMapTypeSheet = () => {
    this.setState((prev) => ({
      showMapTypeSheet: !prev.showMapTypeSheet,
    }));
  };

  selectMapType = (mapType) => {
    this.setState({
      mapType,
      showMapTypeSheet: false,
    });
  };

  renderMapTypeSheet() {
    const { showMapTypeSheet, mapType } = this.state;
    if (!showMapTypeSheet) return null;

    return (
      <MapTypeModalBackdrop onClick={this.toggleMapTypeSheet}>
        <MapTypeSheet onClick={(e) => e.stopPropagation()}>
          <SheetHandle />
          <SheetTitle>Map Types</SheetTitle>

          <MapTypeGrid>
            {Object.values(MAP_TYPES).map((item) => (
              <MapTypeCard
                key={item.key}
                onClick={() => this.selectMapType(item.key)}
                type="button"
              >
                <MapTypeThumb active={mapType === item.key}>
                  <ThumbImage src={item.thumb} alt={item.label} />
                </MapTypeThumb>
                <MapTypeLabel active={mapType === item.key}>
                  {item.label}
                </MapTypeLabel>
              </MapTypeCard>
            ))}
          </MapTypeGrid>
        </MapTypeSheet>
      </MapTypeModalBackdrop>
    );
  }

  render() {
    return (
      <HeroMapShell>
        <HeroMapWrap ref={this.mapRef} />

        <MapTypeButton
          onClick={this.toggleMapTypeSheet}
          type="button"
          aria-label="Map types"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6" />
            <line x1="9" y1="3" x2="9" y2="18" />
            <line x1="15" y1="6" x2="15" y2="21" />
          </svg>
        </MapTypeButton>

        {this.renderMapTypeSheet()}
      </HeroMapShell>
    );
  }
}

class LiveShare extends Component {
  constructor(props) {
    super(props);

    const shareToken =
      this.props.match?.params?.shareToken ||
      window.location.pathname.split("/live/")[1] ||
      "";

    this.state = {
      shareToken,
      loading: true,
      error: null,
      liveRun: null,
    };

    this.pollTimer = null;
  }

  getLastUpdatedText = (lastPoint) => {
    const ts = lastPoint?.ts;
    if (!ts) return "";

    const diffSec = Math.max(
      0,
      Math.floor((Date.now() - new Date(ts).getTime()) / 1000),
    );

    if (diffSec < 10) return "Live now";
    if (diffSec < 60) return `Updated ${diffSec}s ago`;

    const diffMin = Math.floor(diffSec / 60);
    return `Updated ${diffMin}m ago`;
  };

  componentDidMount() {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });

    this.fetchLiveRun();
  }

  componentWillUnmount() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  fetchLiveRun = () => {
    const { shareToken } = this.state;

    axios
      .get(
        `${process.env.REACT_APP_API_URL}users/live-share/public/${shareToken}`,
      )
      .then((response) => {
        const liveRun = response.data;

        if (this.pollTimer) {
          clearTimeout(this.pollTimer);
          this.pollTimer = null;
        }

        this.setState({
          liveRun,
          loading: false,
          error: null,
        });

        if (liveRun?.isLive) {
          this.pollTimer = setTimeout(this.fetchLiveRun, 8000);
        }
      })
      .catch((error) => {
        console.error("Error fetching live share:", error);
        this.setState({
          error: "Failed to load live activity",
          loading: false,
        });
      });
  };

  handleOpenInApp = () => {
    const { shareToken } = this.state;

    ReactGA.event({
      category: "Live Share",
      action: "Open In App Click",
      label: shareToken,
    });

    const deepLink = `bealyo-app://live/${shareToken}`;
    const playStore =
      "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp";

    window.location.href = deepLink;

    setTimeout(() => {
      window.location.href = playStore;
    }, 1500);
  };

  formatDuration = (startedAt, endedAt, isLive) => {
    if (!startedAt) return "--";

    const start = new Date(startedAt).getTime();
    const end = endedAt ? new Date(endedAt).getTime() : Date.now();

    const totalSeconds = Math.max(0, Math.floor((end - start) / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  getOgImage = () => {
    const { liveRun } = this.state;
    if (liveRun?.user?.img_url) return liveRun.user.img_url;
    return "https://www.bealyo.com/images/prebealyoshare.png";
  };

  render() {
    const { loading, error, liveRun, shareToken } = this.state;

    const shareUrl = `https://www.bealyo.com/live/${shareToken}`;
    const ogTitle = "Bealyo Live | Activity in Motion";
    const ogDescription = liveRun
      ? `${liveRun.user?.nickname || "Someone"} is ${liveRun?.isLive ? "sharing a live activity" : "sharing a completed activity"} on Bealyo.`
      : "Follow a live activity on Bealyo.";

    const distanceKm =
      typeof liveRun?.totalDistanceKm === "number"
        ? liveRun.totalDistanceKm.toFixed(2)
        : "0.00";

    const pointCount = Array.isArray(liveRun?.points)
      ? liveRun.points.length
      : 0;

    const hasPoints =
      Array.isArray(liveRun?.points) && liveRun.points.length > 0;

    return (
      <PageContainer>
        <Helmet>
          <title>{ogTitle}</title>
          <meta name="description" content={ogDescription} />

          <meta property="al:android:package" content="com.bealyo.bealyoapp" />
          <meta
            property="al:android:url"
            content={`bealyo-app://live/${shareToken}`}
          />
          <meta property="al:android:app_name" content="Bealyo" />

          <meta property="og:type" content="website" />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={this.getOgImage()} />

          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={shareUrl} />
          <meta property="twitter:title" content={ogTitle} />
          <meta property="twitter:description" content={ogDescription} />
          <meta property="twitter:image" content={this.getOgImage()} />
        </Helmet>

        <GlobalStyle />

        <CenteredContainer>
          <Card>
            {loading ? (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                Loading live activity...
              </div>
            ) : error ? (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                {error}
              </div>
            ) : (
              <>
                <BrandBadge>Bealyo Live</BrandBadge>

                <Hero>
                  <HeroOverlay />
                  <HeroContent>
                    <TopRow>
                      <RunnerBox>
                        <Avatar>
                          {liveRun?.user?.img_url ? (
                            <AvatarImg
                              src={
                                process.env.REACT_APP_IMG_FOLDER +
                                liveRun.user.img_url
                              }
                              alt={liveRun?.user?.nickname || "Runner"}
                            />
                          ) : (
                            <AvatarFallback>
                              {(liveRun?.user?.nickname || "B")
                                .charAt(0)
                                .toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>

                        <div>
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: 900,
                              lineHeight: 1.1,
                              letterSpacing: 0.2,
                            }}
                          >
                            {liveRun?.user?.nickname ||
                              liveRun?.user?.name ||
                              "Bealyo Member"}
                          </div>

                          <SmallText style={{ marginTop: 4 }}>
                            {liveRun?.isLive
                              ? "Live now · " +
                                this.getLastUpdatedText(liveRun?.lastPoint)
                              : "Session completed"}
                          </SmallText>
                        </div>
                      </RunnerBox>

                      <StatusPill live={liveRun?.isLive}>
                        <Dot live={liveRun?.isLive} />
                        {liveRun?.isLive ? "Live now" : "Finished"}
                      </StatusPill>
                    </TopRow>

                    <div style={{ marginTop: 16 }}>
                      {hasPoints ? (
                        <LiveRouteMap
                          points={liveRun.points}
                          lastPoint={liveRun.lastPoint}
                          isLive={liveRun.isLive}
                        />
                      ) : (
                        <SimpleMapBox style={{ minHeight: 220, marginTop: 0 }}>
                          <div>
                            <div
                              style={{
                                fontWeight: 800,
                                fontSize: "18px",
                                marginBottom: 8,
                              }}
                            >
                              Waiting for live route
                            </div>
                            <SmallText>
                              Once the first points arrive, the live route will
                              appear here.
                            </SmallText>
                          </div>
                        </SimpleMapBox>
                      )}
                    </div>

                    <MetricsGrid>
                      <MetricCard>
                        <MetricLabel>Distance</MetricLabel>
                        <MetricValue>{distanceKm} km</MetricValue>
                      </MetricCard>

                      <MetricCard>
                        <MetricLabel>Duration</MetricLabel>
                        <MetricValue>
                          {this.formatDuration(
                            liveRun?.startedAt,
                            liveRun?.endedAt,
                            liveRun?.isLive,
                          )}
                        </MetricValue>
                      </MetricCard>

                      <MetricCard>
                        <MetricLabel>Points received</MetricLabel>
                        <MetricValue>{pointCount}</MetricValue>
                      </MetricCard>
                    </MetricsGrid>
                  </HeroContent>
                </Hero>

                <Description>
                  {liveRun?.isLive
                    ? "This activity is currently being shared live. Open it in the app for the full Bealyo experience."
                    : "This live activity has ended. You can still open it in Bealyo and view the session."}
                </Description>

                <Section>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        alignSelf: "flex-start",
                        background: "#1d9bf0",
                        color: "#fff",
                        borderRadius: "999px",
                        padding: "4px 10px",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      Android Open Testing
                    </span>

                    <Button
                      onClick={() =>
                        window.open(
                          "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp",
                          "_blank",
                        )
                      }
                    >
                      Get Bealyo
                    </Button>

                    <Button secondary onClick={this.handleOpenInApp}>
                      Open in App
                    </Button>

                    <SmallText>
                      Available on Android for now. iOS later.
                    </SmallText>
                  </div>
                </Section>
              </>
            )}
          </Card>
        </CenteredContainer>
      </PageContainer>
    );
  }
}

export default LiveShare;
